import * as React from "react";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import {lazyInject} from "@athena-portal/core";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import * as moment from "moment";
import {
    Policy,
    POLICY_STATUS_MTA,
    PolicyPropertyEnum,
    PREMIUM_TYPE_ANNUAL,
    PremiumType,
    Renewal,
    UPFRONT_PAYMENT_THRESHOLD
} from "@ferry/journey-services";
import {FalconNavigatorHeader, Footer} from "@ferry/portal-core";
import {YourCarCoverPanel} from "./YourCarCoverPanel";
import {
    allowToRenderFutureMTAExplainedHeader,
    getAmountWithDecimalDigits,
    validPolicy
} from "../../../../../CommonFunctions";
import {StorageSuccessModal} from "../../../../../components/modals/StorageSuccessModal";
import {EditCarMtaSuccessModalWordingGenerator} from "../yourCarsPage/modal/EditCarMtaSuccessModalWordingGenerator";
import {FutureMTAExplainedHeader} from "../yourCarsPage/FutureMTAExplainedHeader";
import * as successModals from "../../../../../components/modals/SuccessModalRenderers";
import {pushEventToAdobeDataLayerTimeout} from "../../../../../utils/AdobeDataLayerHelper";
import {Types} from "../../../../../Types";
import type {ICustomerPortalService} from "../../../../../services/ICustomerPortalService";

interface IParams {
    policyNumber: string;
    [key: string]: string | undefined
}

interface IState {
    policy?: Policy,
    renewalProposal?: Renewal;
    policiesProcessed: boolean;
    renewalEnabled: boolean;
    versionHistory?: Policy[];
}

@authorize
export class YourCarCoverPagePlain extends React.Component<IWithRouterProps<IParams>, IState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    private pushAdobeDataLayerTimeout: any;

    constructor(props: IWithRouterProps<IParams>) {
        super(props);

        this.state = {
            policiesProcessed: false,
            renewalEnabled: false
        };
    }

    public componentDidMount(): void {
        const policyNumber: string = this.props.params.policyNumber;

        this.pluginTemplates.setPageTitle("Policy summary");

        this.customerPortalService.getPolicy(policyNumber).subscribe((policy: Policy) => {

            policy.policyProperties.forEach(property => {
                if (property.key === PolicyPropertyEnum.PERFORM_RENEWAL) {
                    this.setState({renewalEnabled: property.value === 'true'});
                }
            });

            this.customerPortalService.getRenewalProposal(policyNumber).subscribe(
                (renewalProposal: Renewal) => {
                    this.setState({
                        renewalProposal
                    })
                }
            );

            this.customerPortalService.getPolicyHistory(policyNumber).subscribe((versions: Policy[]) => {
                this.setState({
                    versionHistory: versions
                })
            });

            this.setState({
                policy,
                policiesProcessed: true
            })
        });

        const successModalRef = window.sessionStorage.getItem("referrerJourney");
        if (window.sessionStorage.getItem("showPopup") === "true" && successModalRef?.startsWith("mta")) {
            this.pushAdobeDataLayerTimeout = pushEventToAdobeDataLayerTimeout(2000, "mta_completed");
        }
    }

    public componentWillUnmount() {
        clearTimeout(this.pushAdobeDataLayerTimeout);
    }

    public render() {
        const {policy, policiesProcessed} = this.state;
        const policyNumber: string = this.props.params.policyNumber;

        const showSuccessModal: boolean = window.sessionStorage.getItem("showPopup") === "true";

        if (policy && policiesProcessed) {
            const policyEndDate: string = policy.endDate;

            return (
                <React.Fragment>
                    {showSuccessModal && this.renderSuccessModal()}
                    <FalconHeader/>
                    <Overlay show={!policiesProcessed}/>
                    <div className="page-contents">
                        <FalconNavigatorHeader
                            back={`/dashboard/annualMotor/${policy.policyNumber}`}
                            title={"Policy summary"}
                        />
                        <div className="page-heading-white"><span
                            className="font-weight-light">Policy no: </span>{policyNumber}</div>
                        <br/>
                        {
                            allowToRenderFutureMTAExplainedHeader(policy.interaction.process) &&
                            <FutureMTAExplainedHeader
                                policyNumber={this.props.params.policyNumber}
                                policyEndDate={policyEndDate}
                                upcomingMtaDate={policy.annualPolicyInfo.maxFutureMtaDate}
                            />
                        }
                        {this.renderYourCarCoverPanels()}
                        {this.pluginTemplates.renderBackLink("Back", false, `/dashboard/annualMotor/${policy.policyNumber}`)}
                    </div>
                    <Footer/>
                </React.Fragment>
            );
        } else {
            return <Overlay show={true}/>
        }
    }

    private renderSuccessModal = (): React.ReactNode => {
        const referrerJourney: string | null = window.sessionStorage.getItem("referrerJourney");

        switch (referrerJourney) {
            case "mtaChangeAddressAnnualMotor":
                return this.renderChangeAddressPopup();
            default:
                return null;
        }
    };

    private renderYourCarCoverPanels() {
        const {policy} = this.state;
        let currentlyActivePolicy: Policy | undefined;

        if (!policy) {
            return;
        }

        if (policy.status === POLICY_STATUS_MTA && moment().isBefore(policy.interaction.headerInfo.startDate) && this.state.versionHistory) {
            currentlyActivePolicy = validPolicy(this.state.versionHistory!, policy.policyNumber);
        }

        return (
            <YourCarCoverPanel
                key={policy.policyNumber}
                policy={policy}
                currentlyActivePolicy={currentlyActivePolicy}
                annualPolicyInfo={policy.annualPolicyInfo}
                vehicleIndex={0}
                renewalProposal={this.state.renewalProposal}
                renewalEnabled={this.state.renewalEnabled}
                showMtcPrice={false}
            />
        );
    }

    private renderChangeAddressPopup = (): React.ReactNode => {
        const interaction: any = this.state.policy?.interaction;
        const {factors, uistate} = interaction;
        const instalmentsNotPaidOff: boolean = interaction.factors.premiumType === PremiumType.monthly && interaction.paymentDetails.instalmentPlan.totalAmount > 0;

        if (factors.premiumType === PREMIUM_TYPE_ANNUAL) {
            return this.getChangeAddressContentForAnnualPlan(factors);
        } else {
            return this.getChangeAddressContentForMonthlyPlan(factors, uistate, instalmentsNotPaidOff);
        }
    };

    private getChangeAddressContentForAnnualPlan(factors: any) {
        let title: React.ReactNode;
        let modalContent: React.ReactNode;
        const renewalWording = this.getRenewalWording();
        if (((factors.returnPremium && factors.returnPremium > 0) || (factors.additionalPremium && factors.additionalPremium > 0)) &&
            (factors.mtaAndDebtCombinedAdditionalAmount && factors.mtaAndDebtCombinedAdditionalAmount > 0)) {

            title = "Payment was successful";
            modalContent = (
                <>
                    <p>
                        <span className="font-weight-bold">{getAmountWithDecimalDigits(factors.mtaAndDebtCombinedAdditionalAmount)}</span> has been taken out from your card
                        for changing address on your car policy. Your account is now up to date.
                    </p>
                    {renewalWording}
                </>);
        } else if (factors.amountToRefund && factors.amountToRefund > 0) {

            title = "Your refund is on the way";
            modalContent = (
                <>
                    <p>
                        We’ve saved your changes, and we’ll refund you <span className="font-weight-bold">&nbsp;{getAmountWithDecimalDigits(factors.amountToRefund)}&nbsp;</span> for changing your address. Please allow two days for it to arrive.
                    </p>
                    {renewalWording}
                </>);
        } else if (factors.additionalPremium && factors.additionalPremium > 0) {

            title = "Payment was successful";
            modalContent =
                <>
                    <p>
                        <span className="font-weight-bold">{getAmountWithDecimalDigits(factors.additionalPremium)}&nbsp;</span> has been taken out from your card for changing address on your car policy.
                    </p>
                    {renewalWording}
                </>
        } else {
            title = "All done";
            modalContent =
                <>
                    <p>
                        <span className="font-weight-bold">
                            Your new address has been changed and saved
                        </span>
                    </p>
                    {renewalWording}
                </>
        }

        return <StorageSuccessModal title={title} explanationWording={modalContent}
                                    anythingElseTitle={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle()}
                                    explanationAnythingElseWording={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody()}/>
    }

    private getChangeAddressContentForMonthlyPlan(factors: any, uistate: any, instalmentsNotPaidOff: boolean) {
        let title: React.ReactNode;
        let modalContent: React.ReactNode;
        const renewalWording = this.getRenewalWording();
        const renewalPremium = this.state.renewalProposal?.factors.annualPremium;

        if (((factors.returnPremium && factors.returnPremium > 0) || (factors.additionalPremium && factors.additionalPremium > 0)) &&
            (factors.mtaAndDebtCombinedAdditionalAmount && factors.mtaAndDebtCombinedAdditionalAmount > 0) &&
            (factors.upfrontPaymentAmount && factors.upfrontPaymentAmount === 0 || factors.upfrontPaymentAmount === factors.mtaAndDebtCombinedAdditionalAmount /*mta in debt case*/)) {

            title = "Payment was successful";
            modalContent =
                <>
                    <p>
                        <span className="font-weight-bold">{getAmountWithDecimalDigits(factors.mtaAndDebtCombinedAdditionalAmount)}&nbsp;</span> has been taken out from your card for changing address on your car policy. Your account is now up to date.
                    </p>
                    {renewalWording}
                </>
        } else if (factors.returnPremium && factors.returnPremium > 0 && factors.monthlyPremium === 0) {

            title = "Your refund is on the way";
            modalContent =
                <>
                    <p>
                        We’ve saved your changes, and we’ll refund you <span className="font-weight-bold">{getAmountWithDecimalDigits(factors.returnPremium)}</span> for changing your address. Please allow two days for it to arrive.
                    </p>
                    {renewalWording}
                </>
        } else if (factors.returnPremium && factors.returnPremium > 0) {
            title = this.gerChangeAddressRefundContentTitle(factors, instalmentsNotPaidOff);
            modalContent = this.gerChangeAddressRefundContentModal(factors, instalmentsNotPaidOff, renewalPremium);
        } else if (factors.additionalPremium && factors.additionalPremium > 0 && uistate.payFullPrice) {

            title = "Payment was successful";
            modalContent =
                <>
                    <p>
                        <span className="font-weight-bold">{getAmountWithDecimalDigits(factors.additionalPremium)}&nbsp;</span> has been taken out from your card for changing address on your car policy.
                    </p>
                    {renewalWording}
                </>
        } else if (factors.additionalPremium && factors.additionalPremium > 0 && !uistate.payFullPrice) {
            title = this.gerChangeAddressAdditionalPremiumContentTitle(factors);
            modalContent = this.gerChangeAddressAdditionalPremiumContentModal(factors, renewalPremium);
        } else {

            title = "All done";
            modalContent =
                <>
                    <p>
                        <span className="font-weight-bold">
                            Your new address has been changed and saved
                        </span>
                    </p>
                    {renewalWording}
                </>
        }

        return <StorageSuccessModal title={title} explanationWording={modalContent}
                                    anythingElseTitle={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle()}
                                    explanationAnythingElseWording={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody()}/>
    }

    private gerChangeAddressRefundContentTitle(factors: any, instalmentsNotPaidOff: boolean) {
        let title: React.ReactNode;

        if (instalmentsNotPaidOff && factors.monthlyPremium > 0) {
            title = "Your payment plan has been updated";
        } else {
            title = "Your refund is on the way";
        }

        return title;
    }

    private gerChangeAddressRefundContentModal(factors: any, instalmentsNotPaidOff: boolean, renewalPremium: any) {
        if (factors.monthlyPremium === 0) {
            return (<p>
                We’ve saved your changes, and we’ll refund you{' '}
                <span className="font-weight-bold">{getAmountWithDecimalDigits(factors.returnPremium)}</span>{' '}
                for changing your address. Please allow two days for it to arrive.
            </p>);
        }

        const previousPremium: number = this.state.policy?.annualPolicyInfo?.previousMonthlyPremium!;

        return successModals.paymentPlanUpdatedRefundModal(factors.returnPremium, factors.monthlyPremium, previousPremium, instalmentsNotPaidOff, renewalPremium);
    }

    private gerChangeAddressAdditionalPremiumContentTitle(factors: any) {
        if (factors.upfrontPaymentAmount >= UPFRONT_PAYMENT_THRESHOLD) {
            return "Payment was successful";
        }
        return "All done";
    }

    private gerChangeAddressAdditionalPremiumContentModal(factors: any, renewalPremium: any) {
        if (factors.upfrontPaymentAmount >= UPFRONT_PAYMENT_THRESHOLD) {
            return (<p>
                        <span
                            className="font-weight-semi-bold">{getAmountWithDecimalDigits(factors.upfrontPaymentAmount)}</span>{' '}
                        has been taken out from your account and{' '}
                        <span
                            className="font-weight-semi-bold">{getAmountWithDecimalDigits(factors.monthlyPaymentSpreadAmount)}</span>{' '}
                        spread onto your monthly payments for changing your address. Your future monthly payment will be{' '}
                        <span
                            className="font-weight-semi-bold">{getAmountWithDecimalDigits(factors.monthlyPremium)} / month.</span>
                    </p>);
        }

        const previousPremium: number = this.state.policy?.annualPolicyInfo?.previousMonthlyPremium!;

        return successModals.paymentPlanUpdatedAdditionalPremiumModal(factors.monthlyPaymentSpreadAmount, factors.monthlyPremium, previousPremium, renewalPremium);
    }

    private getRenewalWording() {
        if (this.state.renewalProposal) {
            const premium = this.state.renewalProposal?.factors.annualPremium;

            return (
                <div className="font-weight-semi-bold">
                    <p>
                        Your next year's renewal price will be {getAmountWithDecimalDigits(premium)}&nbsp;
                    </p>
                    <p>
                        Please check your renewal tile for further details
                    </p>
                </div>
            );
        }
        return <></>
    }

}

export const YourCarCoverPage = withRouter<{}, IParams>(YourCarCoverPagePlain)
